import { Link } from "react-router-dom";
import { ArrowRight } from "@phosphor-icons/react";

import { StoryCard } from "@/components/StoryCard";
import { VerticalVideoFrame } from "@/components/VerticalVideoFrame";
import { Button } from "@/components/ui/button";
import { usePageSeo } from "@/hooks/usePageSeo";
import { NEWSROOM_SECTION_CONFIG, getBreadcrumbItems, getNewsroomStories } from "@/utils/newsroomCollections";

export const NewsSectionPage = ({ feed, sectionKey }) => {
  const section = NEWSROOM_SECTION_CONFIG[sectionKey];
  const stories = getNewsroomStories(feed, sectionKey);
  const featuredStory = stories[0];

  usePageSeo({
    canonicalPath: section.route,
    description: section.description,
    title: `${section.title} | Content Watch HQ`,
    schema: [{
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: getBreadcrumbItems(section.title, section.route).map((item, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: item.label,
        item: `${process.env.REACT_APP_SITE_URL}${item.to}`,
      })),
    }],
  });

  return (
    <main className="page-shell mx-auto max-w-7xl px-4 py-12 sm:px-6 sm:py-14 lg:px-8 lg:py-16" data-testid={`news-section-page-${section.key}`}>
      <section className="grid gap-8 border border-[#D9DCE1] bg-white p-6 shadow-[0_20px_60px_rgba(15,23,42,0.04)] lg:grid-cols-[minmax(0,1fr)_320px] lg:gap-10 lg:p-8" data-testid={`news-section-hero-${section.key}`}>
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#D21404]" data-testid={`news-section-overline-${section.key}`}>{section.overline}</p>
          <h1 className="mt-3 font-[Bebas_Neue] text-5xl uppercase tracking-[0.04em] text-[#0A0A0A] sm:text-6xl" data-testid={`news-section-title-${section.key}`}>{section.title}</h1>
          <p className="mt-4 max-w-3xl text-base leading-8 text-slate-700" data-testid={`news-section-description-${section.key}`}>{section.description}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button asChild className="rounded-none bg-[#1338BE] px-5 py-5 text-xs font-bold uppercase tracking-[0.2em] text-white hover:bg-[#102F9B]" data-testid={`news-section-breaking-button-${section.key}`}>
              <Link to="/breaking-news">
                Back to Breaking News
                <ArrowRight size={16} weight="bold" />
              </Link>
            </Button>
          </div>
        </div>
        {featuredStory ? <VerticalVideoFrame compact story={featuredStory} /> : (
          <div className="border border-[#D9DCE1] bg-[#F8F9FA] p-6" data-testid={`news-section-empty-hero-${section.key}`}>
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#D21404]">Waiting for updates</p>
            <p className="mt-3 text-sm leading-7 text-slate-700">As the newsroom publishes more coverage for this desk, stories will appear here automatically.</p>
          </div>
        )}
      </section>

      <section className="news-card-grid mt-10" data-testid={`news-section-grid-${section.key}`}>
        {stories.length ? stories.map((story) => <StoryCard key={story.id || story.slug} story={story} />) : (
          <div className="border border-[#D9DCE1] bg-white p-8 md:col-span-2 xl:col-span-3" data-testid={`news-section-empty-state-${section.key}`}>
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#D21404]">No stories yet</p>
            <p className="mt-3 text-base leading-8 text-slate-700">This desk is ready for Houston coverage. Matching stories will appear as soon as they’re published.</p>
          </div>
        )}
      </section>
    </main>
  );
};